import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown,
  RefreshCw, TrendingUp, TrendingDown, Activity, Check,
  Sunrise, Moon, Search, Undo2, X, HelpCircle, SlidersHorizontal,
} from 'lucide-react';
import { getCachedMovers, getMoversCacheTtlMs, setCachedMovers } from '../utils/moversCache';
import { getCachedSymbolNames, getCachedSymbolNamesSync, getCompanyName, normalizeSymbolNameEntry } from '../utils/symbolNamesCache';
import {
  isNyMarketOpen,
  getLiveMarketQuotesFromClientCache,
  refreshLiveMarketClientCache,
} from '../utils/liveMarketData';
import { useLiveMarketDisplay } from '../hooks/useLiveMarketDisplay';
import { isAuthenticated } from '../utils/authFetch';
import {
  getCurrentNyTradeDateYmd,
  resolveMarketForStock,
  getTradeDateForMarket,
  previousListedTradeDate,
  ymdFromDateLike,
} from '../utils/marketTradeDates';
import {
  getCachedTradeDates,
  getCachedLatestTradeDatesByMarket,
  getLatestTradeDatesByMarketSync,
} from '../utils/tradeDatesCache';
import SymbolAutocomplete from './SymbolAutocomplete';

const ITEMS_PER_PAGE = 10;
const TOP_N = 50;

const TABS = [
  { key: 'gainers', label: 'Top Gainers', icon: TrendingUp, colorClass: 'movers-tab-gainers' },
  { key: 'losers',  label: 'Top Losers',  icon: TrendingDown, colorClass: 'movers-tab-losers' },
  { key: 'active',  label: 'Most Active', icon: Activity, colorClass: 'movers-tab-active' },
];

/** Extended-hours sessions only expose gainers/losers (no most-active). */
const EXTENDED_TABS = TABS.filter((t) => t.key !== 'active');

/** Trading-session selector (segmented control, like the screener asset-type tabs). */
const SESSION_TABS = [
  { key: 'regular', label: 'Market Hours', icon: Activity },
  { key: 'premarket', label: 'Pre-Market', icon: Sunrise },
  { key: 'afterhours', label: 'After Hours', icon: Moon },
];

/** Market-cap size buckets (raw market_cap in dollars). None selected = show all. */
const MARKET_CAP_BUCKETS = [
  { key: 'micro', label: 'Micro-cap', title: 'Micro-cap: 50 million to 300 million', min: 50e6, max: 300e6 },
  { key: 'small', label: 'Small-cap', title: 'Small-cap: 300 million to 2 billion', min: 300e6, max: 2e9 },
  { key: 'mid', label: 'Mid-cap', title: 'Mid-cap: 2 billion to 10 billion', min: 2e9, max: 10e9 },
  { key: 'large', label: 'Large-cap', title: 'Large-cap: 10 billion to 200 billion', min: 10e9, max: 200e9 },
  { key: 'mega', label: 'Mega-cap', title: 'Mega-cap: 200 billion and greater', min: 200e9, max: null },
];

const MOVERS_FILTER_KEYS = ['close', 'market_cap', 'pct_change', 'exchange'];
const MOVERS_FILTER_LABELS = {
  close: 'Price',
  market_cap: 'Market Cap (M)',
  pct_change: '% Change',
  exchange: 'Exchange',
};
const MOVERS_FILTER_HELP = {
  close: 'Price in dollars. Enter min/max (e.g. 10 to 500).',
  market_cap: 'Market capitalization in millions (M). Enter min/max range.',
  pct_change: 'Percent change from previous close. Enter min/max (e.g. -10 to 20).',
  exchange: 'Listing exchange. Select NYSE, NASDAQ, AMEX, TSX, or All.',
};
const EXCHANGE_OPTIONS = ['All', 'NASDAQ', 'NYSE', 'AMEX', 'TSX'];
const EXCHANGE_OPTION_LABELS = {
  All: 'All Exchanges',
  NASDAQ: 'Nasdaq',
  NYSE: 'NYSE',
  AMEX: 'AMEX',
  TSX: 'TSX',
};

const DEFAULT_SORT_FIELD = 'symbol';
const DEFAULT_SORT_ORDER = 'asc';

const MOVERS_SORT_COLUMNS = [
  { key: 'symbol', label: 'Symbol', title: 'Stock symbol', type: 'string' },
  { key: 'name', label: 'Company', title: 'Company name', type: 'string' },
  { key: 'market_cap', label: 'Market Cap', title: 'Market capitalization', type: 'number' },
  { key: 'exchange', label: 'Exchange', title: 'Exchange', type: 'string' },
  { key: 'pct_change', label: '% Change', title: '% Change from previous close', type: 'number' },
  { key: 'price', label: 'Price', title: 'Stock price', type: 'number' },
  { key: 'last_close', label: 'Prev Close', title: 'Previous close', type: 'number' },
  { key: 'date', label: 'Date', title: 'Trade date / time', type: 'date' },
];

const emptyMoversFilters = () =>
  MOVERS_FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: '' }), {});

const stockMatchesMarketCapBucket = (marketCap, bucket) => {
  const cap = parseFloat(marketCap);
  if (isNaN(cap)) return false;
  if (bucket.max == null) return cap >= bucket.min;
  return cap >= bucket.min && cap <= bucket.max;
};

const formatMarketCap = (marketCap) => {
  if (marketCap == null || marketCap === '') return '--';
  const n = Number(marketCap);
  if (!Number.isFinite(n)) return '--';
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toFixed(2);
};

const formatPrice = (price) => {
  const n = Number(price);
  return Number.isFinite(n) ? n.toFixed(2) : '--';
};

const formatMoversRange = (range) => {
  if (!range) return null;
  const min = range.min !== undefined && range.min !== '' ? range.min : null;
  const max = range.max !== undefined && range.max !== '' ? range.max : null;
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min} – ${max}`;
  if (min != null) return `≥ ${min}`;
  return `≤ ${max}`;
};

const resolveSymbolMapKey = (raw, symbolMap) => {
  if (!raw || !symbolMap) return null;
  const t = raw.trim();
  if (symbolMap[t]) return t;
  return Object.keys(symbolMap).find((k) => k.toUpperCase() === t.toUpperCase()) || null;
};

const compareSortValues = (a, b, type, order) => {
  const aEmpty = a == null || a === '' || a === '--';
  const bEmpty = b == null || b === '' || b === '--';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let cmp = 0;
  if (type === 'number') {
    cmp = Number(a) - Number(b);
    if (!Number.isFinite(cmp)) cmp = 0;
  } else if (type === 'date') {
    cmp = String(a).localeCompare(String(b));
  } else {
    cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  }
  return order === 'desc' ? -cmp : cmp;
};

/** Best-effort market_cap lookup from client live-market cache (may be stale). */
const getLiveMarketCapLookup = () => {
  try {
    const raw = localStorage.getItem('liveMarketQuotesCache');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const quotes = parsed?.quotes;
    if (!quotes || typeof quotes !== 'object') return {};
    const out = {};
    for (const [id, q] of Object.entries(quotes)) {
      if (q && q.market_cap != null && Number.isFinite(Number(q.market_cap))) {
        out[id] = Number(q.market_cap);
      }
    }
    return out;
  } catch {
    return {};
  }
};

const enrichWithCompanyInfo = (rows, symbolMap, marketCapLookup = {}) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((stock) => {
    const id = stock.symbol_id || stock.symbol;
    const fromCache = id ? getCompanyName(id) : null;
    const fromMap = id && symbolMap && symbolMap[id]
      ? (typeof symbolMap[id] === 'string'
          ? symbolMap[id]
          : (symbolMap[id].company_name || symbolMap[id].name || ''))
      : '';
    const name = stock.name || fromCache || fromMap || '';
    const market_cap = stock.market_cap != null && stock.market_cap !== ''
      ? Number(stock.market_cap)
      : (id && marketCapLookup[id] != null ? marketCapLookup[id] : null);
    return {
      ...stock,
      name,
      market_cap: Number.isFinite(market_cap) ? market_cap : null,
    };
  });
};

const quotesToRows = (quotes) => {
  if (!quotes || typeof quotes !== 'object') return [];
  return Object.values(quotes)
    .filter((q) => q && q.symbol_id && Number.isFinite(Number(q.pct_change)) && Number.isFinite(Number(q.close)))
    .map((q) => ({
      symbol_id: q.symbol_id,
      symbol: q.symbol,
      name: q.name || '',
      pct_change: Number(q.pct_change),
      price: Number(q.close),
      last_close: q.prev_close != null ? Number(q.prev_close) : null,
      exchange: q.exchange || '',
      date: q.trade_timestamp || '',
      market_cap: q.market_cap != null ? Number(q.market_cap) : null,
      volume: q.volume != null ? Number(q.volume) : null,
    }));
};

/** Extended (premarket/afterhours) quotes → mover rows, sorted by % change desc. */
const extQuotesToRows = (quotes) => {
  if (!quotes || typeof quotes !== 'object') return [];
  return Object.values(quotes)
    .filter((q) => q && q.symbol_id && Number.isFinite(Number(q.pct_change_ext)) && Number.isFinite(Number(q.price_ext)))
    .map((q) => ({
      symbol_id: q.symbol_id,
      symbol: q.symbol,
      name: q.name || '',
      pct_change: Number(q.pct_change_ext),
      price: Number(q.price_ext),
      last_close: q.prev_regular_close != null ? Number(q.prev_regular_close) : null,
      exchange: q.exchange || '',
      date: q.trade_timestamp_ext || '',
      market_cap: q.market_cap != null ? Number(q.market_cap) : null,
      volume: q.volume_ext != null ? Number(q.volume_ext) : null,
    }))
    .sort((a, b) => b.pct_change - a.pct_change);
};

const liveHasExtQuotes = (live) =>
  Boolean(
    (live?.premarketQuotes && Object.keys(live.premarketQuotes).length > 0) ||
    (live?.afterhoursQuotes && Object.keys(live.afterhoursQuotes).length > 0)
  );

const extRowsFromClientCache = () => {
  if (!isAuthenticated()) return { premarket: [], afterhours: [] };
  const live = getLiveMarketQuotesFromClientCache();
  return {
    premarket: live?.premarketQuotes ? extQuotesToRows(live.premarketQuotes) : [],
    afterhours: live?.afterhoursQuotes ? extQuotesToRows(live.afterhoursQuotes) : [],
  };
};

const resolveStockExchange = (stock) => {
  const raw = String(stock?.exchange || '').trim();
  if (raw) return raw;
  const id = String(stock?.symbol_id || '');
  const i = id.lastIndexOf('_');
  return i >= 0 ? id.slice(i + 1) : '';
};

const stockMatchesExchange = (stock, exchange) => {
  if (!exchange) return true;
  const needle = String(exchange).trim().toUpperCase();
  const hay = resolveStockExchange(stock).toUpperCase();
  const id = String(stock?.symbol_id || '').toUpperCase();
  if (!needle) return true;
  if (hay === needle || hay.includes(needle) || id.endsWith(`_${needle}`)) return true;
  if (needle === 'TSX' && (hay.includes('TORONTO') || hay === 'XTSE')) return true;
  if (needle === 'AMEX' && (hay.includes('AMERICAN') || hay === 'ASE' || hay === 'NYSEAMERICAN' || hay === 'NYSE MKT')) return true;
  if (needle === 'NASDAQ' && (hay === 'NMS' || hay === 'NGS' || hay === 'NCM' || hay.startsWith('NASDAQ'))) return true;
  if (needle === 'NYSE' && hay.startsWith('NYSE') && !hay.includes('AMERICAN') && hay !== 'NYSEAMERICAN') return true;
  return false;
};

const filterByMarketCaps = (rows, selectedKeys) => {
  if (!selectedKeys.length) return rows;
  return rows.filter((stock) =>
    selectedKeys.some((key) => {
      const bucket = MARKET_CAP_BUCKETS.find((b) => b.key === key);
      return bucket ? stockMatchesMarketCapBucket(stock.market_cap, bucket) : false;
    })
  );
};

const stockMatchesRange = (stockValue, range) => {
  if (!range) return true;
  if (stockValue == null || Number.isNaN(stockValue)) return true;
  const min = range.min !== undefined && range.min !== '' ? parseFloat(range.min) : null;
  const max = range.max !== undefined && range.max !== '' ? parseFloat(range.max) : null;
  if (min !== null && Number.isFinite(min) && (max === null || Number.isNaN(max))) {
    return stockValue >= min;
  }
  if (max !== null && Number.isFinite(max) && (min === null || Number.isNaN(min))) {
    return stockValue <= max;
  }
  if (min !== null && max !== null && Number.isFinite(min) && Number.isFinite(max)) {
    return stockValue >= min && stockValue <= max;
  }
  return true;
};

/** Afterhours starts at 16:00 ET; before that, leftover AH prints may still be from yesterday. */
const NY_PREMARKET_OPEN_MINUTES = 4 * 60;
const NY_MARKET_OPEN_MINUTES = 9 * 60 + 30;
const NY_AFTERHOURS_START_MINUTES = 16 * 60;

const getNyNow = () =>
  new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));

const getNyMinutesNow = () => {
  const ny = getNyNow();
  return ny.getHours() * 60 + ny.getMinutes();
};

const isNyWeekday = () => {
  const day = getNyNow().getDay();
  return day !== 0 && day !== 6;
};

const rowTradeDateYmd = (stock) => {
  const raw = String(stock?.date || '').trim();
  if (!raw) return '';
  const ymd = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  }
  return '';
};

const latestTradeDateYmd = (rows) => {
  let latest = '';
  for (const row of rows) {
    const ymd = rowTradeDateYmd(row);
    if (ymd && ymd > latest) latest = ymd;
  }
  return latest;
};

/**
 * Regular: this market's latest trade date; plus today while regular hours are live.
 * Premarket: latest trade date (last save on weekends/holidays); plus today while live.
 * Afterhours: latest trade date; today during AH; previous listed trade date before 16:00 ET.
 * If nothing matches (stale last-save), keep the newest valid US/CA trade date in the snapshot.
 */
const stockMatchesSessionTradeDate = (stock, session, ctx) => {
  const ymd = rowTradeDateYmd(stock);
  if (!ymd) return false;
  const {
    todayNy,
    minutes,
    latestByMarket,
    tradeDates,
    inPremarket,
    inRegular,
    inAfterhours,
  } = ctx;
  const market = resolveMarketForStock(stock);
  const latest = getTradeDateForMarket(latestByMarket, market);

  if (latest && ymd === latest) return true;

  if (session === 'afterhours') {
    if (ymd === todayNy && (inAfterhours || (isNyWeekday() && minutes >= NY_AFTERHOURS_START_MINUTES))) {
      return true;
    }
    if (minutes < NY_AFTERHOURS_START_MINUTES) {
      const prev = (latest && latest < todayNy)
        ? latest
        : previousListedTradeDate(tradeDates, todayNy);
      if (prev && ymd === prev) return true;
    }
    return false;
  }

  if (session === 'premarket') {
    return Boolean(inPremarket && ymd === todayNy);
  }

  if (inRegular && ymd === todayNy) return true;
  return false;
};

const lastSaveFallbackDate = (rows, latestByMarket, tradeDates) => {
  const listed = new Set();
  for (const d of tradeDates || []) {
    const ymd = ymdFromDateLike(d);
    if (ymd) listed.add(ymd);
  }
  const latestUs = ymdFromDateLike(latestByMarket?.US);
  const latestCa = ymdFromDateLike(latestByMarket?.CA);
  let fallback = '';
  for (const row of rows) {
    const ymd = rowTradeDateYmd(row);
    if (!ymd) continue;
    const valid = listed.has(ymd) || ymd === latestUs || ymd === latestCa;
    if (valid && ymd > fallback) fallback = ymd;
  }
  return fallback || latestTradeDateYmd(rows);
};

const filterBySessionTradeDate = (rows, session, tradeDateCtx) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const todayNy = getCurrentNyTradeDateYmd();
  const minutes = getNyMinutesNow();
  const weekday = isNyWeekday();
  const ctx = {
    todayNy,
    minutes,
    latestByMarket: tradeDateCtx?.latestByMarket || null,
    tradeDates: tradeDateCtx?.tradeDates || [],
    inPremarket: weekday
      && minutes >= NY_PREMARKET_OPEN_MINUTES
      && minutes < NY_MARKET_OPEN_MINUTES,
    inRegular: weekday
      && minutes >= NY_MARKET_OPEN_MINUTES
      && minutes < NY_AFTERHOURS_START_MINUTES,
    inAfterhours: weekday
      && minutes >= NY_AFTERHOURS_START_MINUTES
      && minutes < 20 * 60,
  };

  const matched = rows.filter((stock) => stockMatchesSessionTradeDate(stock, session, ctx));
  if (matched.length) return matched;

  const fallback = lastSaveFallbackDate(rows, ctx.latestByMarket, ctx.tradeDates);
  if (!fallback) return [];
  return rows.filter((stock) => rowTradeDateYmd(stock) === fallback);
};

const filterMoversRows = (rows, filterState, session = 'regular') => {
  const {
    selectedMarketCaps = [],
    searchQuery = '',
    selectedSymbol = null,
    filters = {},
    comparisons = {},
    latestByMarket = null,
    tradeDates = [],
  } = filterState || {};

  let out = filterBySessionTradeDate(rows, session, { latestByMarket, tradeDates });
  out = filterByMarketCaps(out, selectedMarketCaps);

  if (filters.exchange) {
    out = out.filter((stock) => stockMatchesExchange(stock, filters.exchange));
  }

  out = out.filter((stock) => {
    const closeRange = comparisons.close;
    const mcapRange = comparisons.market_cap;
    const pctRange = comparisons.pct_change;
    const price = parseFloat(stock.price);
    const mcapM = stock.market_cap != null ? Number(stock.market_cap) / 1e6 : NaN;
    const pct = parseFloat(stock.pct_change);
    return (
      stockMatchesRange(Number.isFinite(price) ? price : null, closeRange) &&
      stockMatchesRange(Number.isFinite(mcapM) ? mcapM : null, mcapRange) &&
      stockMatchesRange(Number.isFinite(pct) ? pct : null, pctRange)
    );
  });

  const q = (searchQuery || '').trim();
  if (selectedSymbol) {
    out = out.filter((stock) => (stock.symbol_id || '') === selectedSymbol);
  } else if (q) {
    const qLower = q.toLowerCase();
    out = out.filter((stock) =>
      (stock.symbol_id || '').toLowerCase().includes(qLower) ||
      (stock.symbol || '').toLowerCase().includes(qLower) ||
      (stock.name || '').toLowerCase().includes(qLower)
    );
  }

  return out;
};

const buildListsFromRows = (rows, filterState, session = 'regular') => {
  const filtered = filterMoversRows(rows, filterState, session);
  const cap = filterState?.selectedSymbol ? Math.max(filtered.length, 1) : TOP_N;
  const gainers = [...filtered]
    .sort((a, b) => b.pct_change - a.pct_change)
    .slice(0, cap);
  const losers = [...filtered]
    .sort((a, b) => a.pct_change - b.pct_change)
    .slice(0, cap);
  const mostActive = [...filtered]
    .filter((r) => r.volume != null && Number.isFinite(r.volume))
    .sort((a, b) => (b.volume || 0) - (a.volume || 0))
    .slice(0, cap);
  return { gainers, losers, mostActive };
};

const buildListsFromApi = (gainers, losers, mostActive, filterState) => ({
  gainers: filterMoversRows(gainers || [], filterState, 'regular').slice(0, TOP_N),
  losers: filterMoversRows(losers || [], filterState, 'regular').slice(0, TOP_N),
  mostActive: filterMoversRows(mostActive || [], filterState, 'regular').slice(0, TOP_N),
});

const MoversFilterModal = ({ isOpen, onClose, onApply, currentFilters, comparisons }) => {
  const [filters, setFilters] = useState(() => ({ ...emptyMoversFilters(), ...(currentFilters || {}) }));
  const [minMaxFilters, setMinMaxFilters] = useState(() => ({ ...(comparisons || {}) }));
  const modalRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleRangeChange = (name, minOrMax, value) => {
    setMinMaxFilters((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        [minOrMax]: value,
      },
    }));
  };

  const handleApply = () => {
    const processedFilters = MOVERS_FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: filters[k] ?? '' }), {});
    const normMinMax = MOVERS_FILTER_KEYS.reduce((acc, k) => {
      const range = minMaxFilters[k];
      if (range && (range.min !== undefined || range.max !== undefined)) acc[k] = range;
      return acc;
    }, {});
    onApply(processedFilters, normMinMax);
    onClose();
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (!prevIsOpenRef.current) {
        setFilters({ ...emptyMoversFilters(), ...(currentFilters || {}) });
        setMinMaxFilters({ ...(comparisons || {}) });
      }
      prevIsOpenRef.current = true;
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      prevIsOpenRef.current = false;
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, currentFilters, comparisons]);

  const resetFilters = () => {
    setFilters(emptyMoversFilters());
    setMinMaxFilters({});
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal screener-filter-modal" ref={modalRef}>
        <div className="modal-content">
          <div className="modal-header screener-filter-modal-header">
            <div className="screener-filter-modal-heading">
              <h3>Filters</h3>
              <p className="screener-filter-modal-sub">Set min/max ranges or pick an exchange, then apply.</p>
            </div>
            <div className="modal-header-buttons">
              <button type="button" onClick={resetFilters} className="reset-button" title="Reset Filters">
                <Undo2 />
              </button>
              <button type="button" onClick={onClose} className="modal-close" aria-label="Close filters">
                <X />
              </button>
            </div>
          </div>
          <div className="modal-body screener-filter-body">
            {MOVERS_FILTER_KEYS.map((key) => {
              const value = filters[key] ?? '';
              const isNumericField = key !== 'exchange';
              const label = MOVERS_FILTER_LABELS[key] || key;
              const helpText = MOVERS_FILTER_HELP[key];

              if (isNumericField) {
                return (
                  <div key={key} className="form-group">
                    <label htmlFor={`movers-${key}`} className="filter-label-with-help">
                      <span>{label}</span>
                      {helpText && (
                        <span className="filter-help-icon" title={helpText} aria-label="Help">
                          <HelpCircle size={13} />
                        </span>
                      )}
                    </label>
                    <div className="filter-range-inputs">
                      <input
                        id={`movers-${key}-min`}
                        type="number"
                        placeholder="Min"
                        value={minMaxFilters[key]?.min || ''}
                        onChange={(e) => handleRangeChange(key, 'min', e.target.value)}
                        className="range-input"
                      />
                      <span className="range-separator">to</span>
                      <input
                        id={`movers-${key}-max`}
                        type="number"
                        placeholder="Max"
                        value={minMaxFilters[key]?.max || ''}
                        onChange={(e) => handleRangeChange(key, 'max', e.target.value)}
                        className="range-input"
                      />
                    </div>
                  </div>
                );
              }

              return (
                <div key={key} className="form-group">
                  <label htmlFor={`movers-${key}`} className="filter-label-with-help">
                    <span>{label}</span>
                    {helpText && (
                      <span className="filter-help-icon" title={helpText} aria-label="Help">
                        <HelpCircle size={13} />
                      </span>
                    )}
                  </label>
                  <div className="filter-input-group">
                    <select
                      id={`movers-${key}`}
                      name={key}
                      value={value}
                      onChange={handleChange}
                      className="sector-select"
                    >
                      {EXCHANGE_OPTIONS.map((option) => (
                        <option key={option} value={option === 'All' ? '' : option}>
                          {EXCHANGE_OPTION_LABELS[option] || option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="modal-footer-buttons movers-filter-footer">
            <button type="button" onClick={handleApply} className="apply-button">
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MoversTable = ({ data, currentPage, setCurrentPage, itemsPerPage }) => {
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState('neutral');
  const totalPages = Math.ceil(data.length / itemsPerPage) || 1;
  const prevDataLengthRef = useRef(data.length);

  useEffect(() => {
    if (prevDataLengthRef.current !== data.length) {
      prevDataLengthRef.current = data.length;
      setCurrentPage(1);
    }
  }, [data.length, setCurrentPage]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages, setCurrentPage]);

  const handleColumnSort = (fieldKey) => {
    setCurrentPage(1);
    if (sortField === fieldKey) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') setSortDirection('neutral');
      else setSortDirection('asc');
    } else {
      setSortField(fieldKey);
      setSortDirection('asc');
    }
  };

  const getSortValue = (item, fieldKey) => {
    switch (fieldKey) {
      case 'symbol': return item.symbol;
      case 'name': return item.name;
      case 'market_cap': return item.market_cap;
      case 'exchange': return item.exchange;
      case 'pct_change': return item.pct_change;
      case 'price': return item.price;
      case 'last_close': return item.last_close;
      case 'date': return item.date;
      default: return item[fieldKey];
    }
  };

  const sortedData = useMemo(() => {
    if (sortDirection === 'neutral') return data;
    const columnMeta = MOVERS_SORT_COLUMNS.find((c) => c.key === sortField);
    const sortType = columnMeta?.type || 'string';
    return [...data].sort((a, b) => {
      const primary = compareSortValues(getSortValue(a, sortField), getSortValue(b, sortField), sortType, sortDirection);
      if (primary !== 0) return primary;
      return compareSortValues(getSortValue(a, DEFAULT_SORT_FIELD), getSortValue(b, DEFAULT_SORT_FIELD), 'string', DEFAULT_SORT_ORDER);
    });
  }, [data, sortField, sortDirection]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.min(Math.max(1, newPage), totalPages));
  };

  const renderSortableHeader = (col) => {
    const isActive = sortDirection !== 'neutral' && sortField === col.key;
    const ariaSort = isActive
      ? (sortDirection === 'asc' ? 'ascending' : 'descending')
      : 'none';

    return (
      <th
        key={col.key}
        title={col.title}
        aria-sort={ariaSort}
        className={
          col.key === 'symbol' ? 'pw-col-symbol' : col.key === 'name' ? 'pw-col-company' : undefined
        }
      >
        <span className="screener-sort-header-inner">
          <span className="screener-sort-label">{col.label}</span>
          <button
            type="button"
            className="screener-sort-trigger"
            onClick={() => handleColumnSort(col.key)}
            aria-label={`Sort by ${col.label}${isActive ? `, ${sortDirection}` : ''}`}
          >
            <span
              className={`screener-sort-arrow screener-sort-arrow--up ${
                isActive && sortDirection === 'asc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'
              }`}
              aria-hidden="true"
            />
            <span
              className={`screener-sort-arrow screener-sort-arrow--down ${
                isActive && sortDirection === 'desc' ? 'screener-sort-icon--active' : 'screener-sort-icon--muted'
              }`}
              aria-hidden="true"
            />
          </button>
        </span>
      </th>
    );
  };

  return (
    <>
      <div className="movers-table-wrap">
        <table className="movers-table screener-sort-table pw-table--sticky-symbol">
          <thead>
            <tr>
              {MOVERS_SORT_COLUMNS.map((col) => renderSortableHeader(col))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((stock, index) => (
              <tr key={stock.symbol_id || `${stock.symbol}-${index}`}>
                <td className="pw-col-symbol">
                  <Link
                    to={`/stock/${encodeURIComponent(String(stock.symbol_id).toUpperCase())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="movers-symbol-link"
                  >
                    {stock.symbol}
                  </Link>
                </td>
                <td className="pw-company-name pw-col-company">{stock.name || '--'}</td>
                <td className="movers-mcap">{formatMarketCap(stock.market_cap)}</td>
                <td className="movers-date">{stock.exchange}</td>
                <td>
                  <span className={`movers-change ${stock.pct_change >= 0 ? 'positive' : 'negative'}`}>
                    {stock.pct_change >= 0 ? '+' : ''}{Number(stock.pct_change).toFixed(2)}%
                  </span>
                </td>
                <td className="movers-price">{formatPrice(stock.price)}</td>
                <td className="movers-price">{formatPrice(stock.last_close)}</td>
                <td className="movers-date">{stock.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pw-pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} title="First Page">
            <ChevronsLeft size={18} />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} title="Previous Page">
            <ChevronLeft size={18} />
          </button>
          <span className="pw-page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} title="Next Page">
            <ChevronRight size={18} />
          </button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} title="Last Page">
            <ChevronsRight size={18} />
          </button>
        </div>
      )}
    </>
  );
};

const Movers = () => {
  const [sessionTab, setSessionTab] = useState('regular'); // 'regular' | 'premarket' | 'afterhours'
  const [activeTab, setActiveTab] = useState('gainers');
  const [sourceMode, setSourceMode] = useState('api'); // 'api' | 'live'
  const [liveRows, setLiveRows] = useState([]);
  const [premarketRows, setPremarketRows] = useState(() => extRowsFromClientCache().premarket);
  const [afterhoursRows, setAfterhoursRows] = useState(() => extRowsFromClientCache().afterhours);
  const [apiGainers, setApiGainers] = useState([]);
  const [apiLosers, setApiLosers] = useState([]);
  const [apiMostActive, setApiMostActive] = useState([]);
  const [selectedMarketCaps, setSelectedMarketCaps] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState(null);
  const [currentFilters, setCurrentFilters] = useState(() => emptyMoversFilters());
  const [comparisons, setComparisons] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [gainersPage, setGainersPage] = useState(1);
  const [losersPage, setLosersPage] = useState(1);
  const [mostActivePage, setMostActivePage] = useState(1);
  const [premarketGainersPage, setPremarketGainersPage] = useState(1);
  const [premarketLosersPage, setPremarketLosersPage] = useState(1);
  const [afterhoursGainersPage, setAfterhoursGainersPage] = useState(1);
  const [afterhoursLosersPage, setAfterhoursLosersPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshIntervalMs, setRefreshIntervalMs] = useState(getMoversCacheTtlMs());
  const [latestByMarket, setLatestByMarket] = useState(() => getLatestTradeDatesByMarketSync());
  const [tradeDates, setTradeDates] = useState([]);
  const sourceModeRef = useRef(sourceMode);

  const authed = isAuthenticated();

  useEffect(() => {
    sourceModeRef.current = sourceMode;
  }, [sourceMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [dates, byMarket] = await Promise.all([
          getCachedTradeDates(),
          getCachedLatestTradeDatesByMarket(),
        ]);
        if (cancelled) return;
        setTradeDates(Array.isArray(dates) ? dates : []);
        setLatestByMarket(byMarket);
      } catch {
        /* keep sync cache / empty */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filterState = useMemo(() => ({
    selectedMarketCaps,
    searchQuery,
    selectedSymbol,
    filters: currentFilters,
    comparisons,
    latestByMarket,
    tradeDates,
  }), [selectedMarketCaps, searchQuery, selectedSymbol, currentFilters, comparisons, latestByMarket, tradeDates]);

  const lists = useMemo(() => {
    if (sourceMode === 'live') {
      return buildListsFromRows(liveRows, filterState, 'regular');
    }
    return buildListsFromApi(apiGainers, apiLosers, apiMostActive, filterState);
  }, [sourceMode, liveRows, apiGainers, apiLosers, apiMostActive, filterState]);

  const { gainers, losers, mostActive } = lists;

  const premarketLists = useMemo(
    () => buildListsFromRows(premarketRows, filterState, 'premarket'),
    [premarketRows, filterState]
  );
  const afterhoursLists = useMemo(
    () => buildListsFromRows(afterhoursRows, filterState, 'afterhours'),
    [afterhoursRows, filterState]
  );

  const tabDataMap = {
    gainers: { data: gainers, page: gainersPage, setPage: setGainersPage },
    losers: { data: losers, page: losersPage, setPage: setLosersPage },
    active: { data: mostActive, page: mostActivePage, setPage: setMostActivePage },
  };

  const premarketTabDataMap = {
    gainers: { data: premarketLists.gainers, page: premarketGainersPage, setPage: setPremarketGainersPage },
    losers: { data: premarketLists.losers, page: premarketLosersPage, setPage: setPremarketLosersPage },
  };

  const afterhoursTabDataMap = {
    gainers: { data: afterhoursLists.gainers, page: afterhoursGainersPage, setPage: setAfterhoursGainersPage },
    losers: { data: afterhoursLists.losers, page: afterhoursLosersPage, setPage: setAfterhoursLosersPage },
  };

  const applyLiveQuotes = useCallback((live) => {
    if (!live?.quotes || !Object.keys(live.quotes).length) return false;
    const rows = quotesToRows(live.quotes);
    if (!rows.length) return false;
    setLiveRows(rows);
    setSourceMode('live');
    setLastUpdated(new Date(live.cachedAt || Date.now()));
    setIsLoading(false);
    setIsRefreshing(false);
    return true;
  }, []);

  /** Premarket/afterhours rows persist — only replaced when new data arrives. */
  const applyExtQuotes = useCallback((live) => {
    if (!live) return;
    if (live.premarketQuotes && Object.keys(live.premarketQuotes).length > 0) {
      const rows = extQuotesToRows(live.premarketQuotes);
      if (rows.length) setPremarketRows(rows);
    }
    if (live.afterhoursQuotes && Object.keys(live.afterhoursQuotes).length > 0) {
      const rows = extQuotesToRows(live.afterhoursQuotes);
      if (rows.length) setAfterhoursRows(rows);
    }
  }, []);

  const fetchMovers = useCallback(async (bypassClientCache = false) => {
    if (!bypassClientCache) {
      const cached = getCachedMovers();
      if (cached) {
        const symbolMap = await getCachedSymbolNames().catch(() => ({}));
        const mcapLookup = getLiveMarketCapLookup();
        setApiGainers(enrichWithCompanyInfo(cached.gainers || [], symbolMap, mcapLookup));
        setApiLosers(enrichWithCompanyInfo(cached.losers || [], symbolMap, mcapLookup));
        setApiMostActive(enrichWithCompanyInfo(cached.mostActive || [], symbolMap, mcapLookup));
        setSourceMode('api');
        if (cached.cachedAt) setLastUpdated(new Date(cached.cachedAt));
        if (cached.cacheTtlMs) setRefreshIntervalMs(cached.cacheTtlMs);
        setIsLoading(false);
        return;
      }
    }
    setIsRefreshing(true);
    try {
      const response = await fetch('/api/movers');
      const data = await response.json();
      const symbolMap = await getCachedSymbolNames().catch(() => ({}));
      const mcapLookup = getLiveMarketCapLookup();
      setApiGainers(enrichWithCompanyInfo(data.gainers || [], symbolMap, mcapLookup));
      setApiLosers(enrichWithCompanyInfo(data.losers || [], symbolMap, mcapLookup));
      setApiMostActive(enrichWithCompanyInfo(data.mostActive || [], symbolMap, mcapLookup));
      setSourceMode('api');
      const serverCachedAt = Number.isFinite(data.cachedAt) ? data.cachedAt : Date.now();
      setLastUpdated(new Date(serverCachedAt));
      setCachedMovers(data, data.cacheTtlMs);
      setRefreshIntervalMs(data.cacheTtlMs || getMoversCacheTtlMs());
    } catch (error) {
      console.error('Error fetching movers:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const tryLiveOrFetch = useCallback(async (bypassClientCache = false) => {
    if (isAuthenticated()) {
      const live = getLiveMarketQuotesFromClientCache();
      applyExtQuotes(live);
      if (isNyMarketOpen() && applyLiveQuotes(live)) return;
      if (live?.lastRegularQuotes && Object.keys(live.lastRegularQuotes).length > 0) {
        if (applyLiveQuotes({ quotes: live.lastRegularQuotes, cachedAt: live.cachedAt })) return;
      }
    }
    await fetchMovers(bypassClientCache);
  }, [applyExtQuotes, applyLiveQuotes, fetchMovers]);

  useEffect(() => {
    tryLiveOrFetch(false);
  }, [tryLiveOrFetch, authed]);

  // Hydrate last-saved premarket/afterhours as soon as auth + client cache are ready
  // (startup used to skip this until Refresh, because the first pass ran before cache load).
  useEffect(() => {
    if (!authed) return undefined;
    let cancelled = false;
    let retryId = 0;
    let attempts = 0;

    const hydrate = () => {
      if (cancelled) return false;
      const live = getLiveMarketQuotesFromClientCache();
      applyExtQuotes(live);
      return liveHasExtQuotes(live);
    };

    const ensureExtCache = async () => {
      if (hydrate() || cancelled) return;
      await refreshLiveMarketClientCache({ force: true });
      if (cancelled) return;
      if (hydrate()) return;
      attempts += 1;
      if (attempts < 8) {
        retryId = window.setTimeout(ensureExtCache, 1500);
      }
    };

    ensureExtCache();
    return () => {
      cancelled = true;
      if (retryId) window.clearTimeout(retryId);
    };
  }, [authed, applyExtQuotes]);

  useLiveMarketDisplay({
    enabled: authed,
    onApply: (live) => {
      applyExtQuotes(live);
      if (isNyMarketOpen()) {
        applyLiveQuotes(live);
        return;
      }
      if (live?.lastRegularQuotes && Object.keys(live.lastRegularQuotes).length > 0) {
        applyLiveQuotes({ quotes: live.lastRegularQuotes, cachedAt: live.cachedAt });
      }
    },
  });

  // When regular hours end, keep last-saved live quotes (weekends / holidays).
  useEffect(() => {
    const id = setInterval(() => {
      if (sourceModeRef.current !== 'live' || isNyMarketOpen()) return;
      const live = getLiveMarketQuotesFromClientCache();
      applyExtQuotes(live);
      if (live?.lastRegularQuotes && Object.keys(live.lastRegularQuotes).length > 0) {
        applyLiveQuotes({ quotes: live.lastRegularQuotes, cachedAt: live.cachedAt });
        return;
      }
      fetchMovers(true);
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [fetchMovers, applyExtQuotes, applyLiveQuotes]);

  // Poll API movers only when not using live market data
  useEffect(() => {
    if (sourceMode === 'live') return undefined;
    const intervalId = setInterval(() => fetchMovers(false), refreshIntervalMs);
    return () => clearInterval(intervalId);
  }, [fetchMovers, refreshIntervalMs, sourceMode]);

  const resetAllPages = () => {
    setGainersPage(1);
    setLosersPage(1);
    setMostActivePage(1);
    setPremarketGainersPage(1);
    setPremarketLosersPage(1);
    setAfterhoursGainersPage(1);
    setAfterhoursLosersPage(1);
  };

  const handleManualRefresh = () => {
    tryLiveOrFetch(true);
    resetAllPages();
  };

  const toggleMarketCapBucket = (key) => {
    setSelectedMarketCaps((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    resetAllPages();
  };

  const handleSessionChange = (key) => {
    setSessionTab(key);
    // Extended sessions only have gainers/losers — leave Most Active if switching back later.
    if (key !== 'regular' && activeTab === 'active') {
      setActiveTab('gainers');
    }
  };

  const handleExchangeChange = (exchange) => {
    setCurrentFilters((prev) => ({ ...prev, exchange }));
    resetAllPages();
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!(query || '').trim()) {
      setSelectedSymbol(null);
      setSelectedCompanyName(null);
      resetAllPages();
      return;
    }
    const symbolMap = getCachedSymbolNamesSync() || {};
    const mapKey = resolveSymbolMapKey(query || '', symbolMap);
    let resolvedSymbol = null;
    let resolvedCompany = null;

    if (mapKey) {
      resolvedSymbol = mapKey;
      resolvedCompany = normalizeSymbolNameEntry(mapKey, symbolMap[mapKey]).company_name;
      setSelectedSymbol(mapKey);
      setSelectedCompanyName(resolvedCompany);
    } else {
      const companyMatch = Object.entries(symbolMap).find(([sid, raw]) => {
        const c = normalizeSymbolNameEntry(sid, raw).company_name;
        return (
          c &&
          (c.toUpperCase() === (query || '').trim().toUpperCase() ||
            c.toLowerCase() === (query || '').trim().toLowerCase() ||
            c === (query || '').trim())
        );
      });
      if (companyMatch) {
        const co = normalizeSymbolNameEntry(companyMatch[0], companyMatch[1]).company_name;
        resolvedSymbol = companyMatch[0];
        resolvedCompany = co;
        setSelectedSymbol(companyMatch[0]);
        setSelectedCompanyName(co);
      } else {
        setSelectedSymbol(null);
        setSelectedCompanyName(null);
      }
    }
    resetAllPages();
    return { symbol: resolvedSymbol, company: resolvedCompany };
  };

  const handleSymbolSelect = (symbolId, company) => {
    const symbolMap = getCachedSymbolNamesSync() || {};
    const key = resolveSymbolMapKey(symbolId || '', symbolMap) || (symbolId || '').trim();
    setSelectedSymbol(key);
    setSelectedCompanyName(company);
    setSearchQuery('');
    resetAllPages();
  };

  const handleFilterChange = (filters, newComparisons) => {
    setCurrentFilters(filters);
    setComparisons(newComparisons);
    resetAllPages();
  };

  const resetFilters = () => {
    setCurrentFilters(emptyMoversFilters());
    setComparisons({});
    setSearchQuery('');
    setSelectedSymbol(null);
    setSelectedCompanyName(null);
    setSelectedMarketCaps([]);
    resetAllPages();
  };

  const removeMoversChip = (chipKey) => {
    if (chipKey === 'search') {
      setSearchQuery('');
      setSelectedSymbol(null);
      setSelectedCompanyName(null);
      resetAllPages();
      return;
    }
    if (chipKey.startsWith('range-')) {
      const rangeKey = chipKey.slice('range-'.length);
      const nextComparisons = { ...comparisons };
      delete nextComparisons[rangeKey];
      setComparisons(nextComparisons);
      resetAllPages();
      return;
    }
    setCurrentFilters((prev) => ({ ...prev, [chipKey]: '' }));
    resetAllPages();
  };

  const formatLastUpdated = (date) => {
    return date.toLocaleTimeString('en-CA');
  };

  const isExtendedSession = sessionTab !== 'regular';
  const extendedTabKey = activeTab === 'losers' ? 'losers' : 'gainers';
  const sessionTabDataMap = sessionTab === 'premarket'
    ? premarketTabDataMap
    : sessionTab === 'afterhours'
      ? afterhoursTabDataMap
      : tabDataMap;
  const listTabs = isExtendedSession ? EXTENDED_TABS : TABS;
  const currentListKey = isExtendedSession ? extendedTabKey : activeTab;
  const currentTab = sessionTabDataMap[currentListKey];
  const currentTitle = TABS.find((t) => t.key === currentListKey)?.label;

  const gainersLen = sessionTabDataMap.gainers?.data?.length || 0;
  const losersLen = sessionTabDataMap.losers?.data?.length || 0;
  const activeLen = sessionTabDataMap.active?.data?.length || 0;

  useEffect(() => {
    if (!selectedSymbol) return;
    const lengths = { gainers: gainersLen, losers: losersLen, active: activeLen };
    if ((lengths[currentListKey] || 0) > 0) return;
    const foundKey = (isExtendedSession ? ['gainers', 'losers'] : ['gainers', 'losers', 'active'])
      .find((key) => (lengths[key] || 0) > 0);
    if (foundKey) setActiveTab(foundKey);
  }, [selectedSymbol, currentListKey, isExtendedSession, gainersLen, losersLen, activeLen]);

  const moversFilterChips = useMemo(() => {
    const chips = [];
    if (searchQuery.trim()) {
      chips.push({ key: 'search', label: 'Search', value: searchQuery.trim() });
    } else if (selectedSymbol) {
      const symbolMap = getCachedSymbolNamesSync() || {};
      const tick =
        normalizeSymbolNameEntry(selectedSymbol, symbolMap[selectedSymbol]).symbol ||
        selectedSymbol;
      chips.push({
        key: 'search',
        label: 'Search',
        value: selectedCompanyName ? `${tick} — ${selectedCompanyName}` : tick,
      });
    }
    if (currentFilters.exchange) {
      chips.push({
        key: 'exchange',
        label: 'Exchange',
        value: EXCHANGE_OPTION_LABELS[currentFilters.exchange] || currentFilters.exchange,
      });
    }
    Object.entries(comparisons).forEach(([key, range]) => {
      const formatted = formatMoversRange(range);
      if (formatted) {
        chips.push({ key: `range-${key}`, label: MOVERS_FILTER_LABELS[key] || key, value: formatted });
      }
    });
    return chips;
  }, [searchQuery, selectedSymbol, selectedCompanyName, currentFilters, comparisons]);

  const modalFilterCount = moversFilterChips.filter((c) => c.key !== 'search').length;
  const hasActiveFilters = moversFilterChips.length > 0 || selectedMarketCaps.length > 0;

  if (isLoading) {
    return (
      <div className="movers-container">
        <div className="movers-loading">
          <RefreshCw size={28} className="spinning" />
          <span>Loading market movers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="movers-container">
      <div className="movers-header">
        <div className="movers-header-left">
          <div className="movers-header-icon">
            <Activity size={24} />
          </div>
          <div>
            <h2 className="movers-title">Market Movers</h2>
            <p className="movers-subtitle">
              {sourceMode === 'live'
                ? 'Live market top performers and most traded'
                : 'Real-time top performers and most traded'}
            </p>
          </div>
        </div>
        <div className="movers-header-actions">
          <span className="movers-last-updated">
            Last Updated {formatLastUpdated(lastUpdated)}
          </span>
        </div>
      </div>

      <div className="movers-toolbar">
        <div className="screener-seg movers-session-seg" role="tablist" aria-label="Trading session">
          {SESSION_TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              role="tab"
              aria-selected={sessionTab === key}
              className={`screener-seg-btn${sessionTab === key ? ' active' : ''}`}
              onClick={() => handleSessionChange(key)}
            >
              <Icon size={14} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
        <div className="screener-search movers-search">
          <Search size={16} className="screener-search-icon" aria-hidden="true" />
          <SymbolAutocomplete
            value={searchQuery}
            onChange={(value) => handleSearch(value)}
            onSelect={handleSymbolSelect}
            placeholder="Search symbol or company"
            className="screener-search-input"
            showValidation={false}
            maxSuggestions={10}
            clearOnSelect
          />
        </div>
        <div className="movers-mcap-filters screener-mcap-filters" role="group" aria-label="Market cap size filters">
          {MARKET_CAP_BUCKETS.map((bucket) => {
            const active = selectedMarketCaps.includes(bucket.key);
            return (
              <button
                key={bucket.key}
                type="button"
                className={`screener-mcap-pill${active ? ' is-active' : ''}`}
                title={bucket.title}
                aria-pressed={active}
                onClick={() => toggleMarketCapBucket(bucket.key)}
              >
                <Check className="screener-mcap-check" size={14} aria-hidden="true" />
                {bucket.label}
              </button>
            );
          })}
        </div>
        <div className="movers-filter-actions">
          <div className="movers-exchange-dropdown screener-field-input">
            <select
              id="movers-exchange-select"
              value={currentFilters.exchange || ''}
              onChange={(e) => handleExchangeChange(e.target.value)}
              className="screener-select"
              aria-label="Exchange"
            >
              {EXCHANGE_OPTIONS.map((option) => (
                <option key={option} value={option === 'All' ? '' : option}>
                  {EXCHANGE_OPTION_LABELS[option] || option}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="screener-field-chevron" aria-hidden="true" />
          </div>
          <button
            type="button"
            onClick={handleManualRefresh}
            className="screener-icon-btn"
            disabled={isRefreshing}
            title="Refresh data"
            aria-label="Refresh market movers"
          >
            <RefreshCw size={24} className={`icon${isRefreshing ? ' spinning' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setIsFilterModalOpen(true)}
            className="screener-btn screener-btn-primary"
          >
            <SlidersHorizontal className="icon" size={24} />
            Filters
            {modalFilterCount > 0 && (
              <span className="screener-filter-count">{modalFilterCount}</span>
            )}
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="screener-icon-btn"
            title="Undo search and filters"
            aria-label="Undo search and filters"
          >
            <Undo2 size={24} className="icon" />
          </button>
        </div>
      </div>

      {hasActiveFilters && moversFilterChips.length > 0 && (
        <div className="screener-chips movers-chips" aria-label="Active filters">
          <span className="screener-chips-label">Active:</span>
          {moversFilterChips.map((chip) => (
            <span key={chip.key} className="screener-chip">
              <span className="screener-chip-label">{chip.label}</span>
              <span className="screener-chip-value">{chip.value}</span>
              <button
                type="button"
                className="screener-chip-remove"
                onClick={() => removeMoversChip(chip.key)}
                aria-label={`Remove ${chip.label} filter`}
                title={`Remove ${chip.label} filter`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button type="button" className="screener-chips-clear" onClick={resetFilters}>
            Clear all
          </button>
        </div>
      )}

      <MoversFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleFilterChange}
        currentFilters={currentFilters}
        comparisons={comparisons}
      />

      <div className="movers-tabs">
        {listTabs.map(({ key, label, icon: Icon, colorClass }) => (
          <button
            key={key}
            className={`movers-tab ${colorClass} ${currentListKey === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={16} />
            <span className="movers-tab-label">{label}</span>
            <span className="movers-tab-count">{sessionTabDataMap[key].data.length}</span>
          </button>
        ))}
      </div>

      <div className="movers-data-card">
        <div className="movers-data-header">
          <h3>{currentTitle}</h3>
          <span className="movers-data-count">
            {currentTab.data.length} {currentTab.data.length === 1 ? 'stock' : 'stocks'}
          </span>
        </div>

        {currentTab.data.length === 0 ? (
          <div className="movers-empty">
            {isExtendedSession && !isAuthenticated()
              ? 'Sign in to view extended hours movers'
              : hasActiveFilters
                ? 'No stocks match the current search or filters'
                : 'No data available'}
          </div>
        ) : (
          <MoversTable
            data={currentTab.data}
            currentPage={currentTab.page}
            setCurrentPage={currentTab.setPage}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        )}
      </div>
    </div>
  );
};

export default Movers;
